import { useMemo, useRef, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ExternalLink, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { EmptyState, Skeleton } from '@/components/ui/misc'
import { cn, formatNumber, highlightSegments, sourceLink } from '@/lib/utils'
import { documentFileUrl } from '@/lib/api/documents/get-documents'
import type { Mention } from '@/types/terms'

const ROW_HEIGHT = 96

interface MentionsListProps {
  mentions: Mention[]
  isLoading: boolean
  termValue: string
  /** Open the document's own text view at the mention's page. */
  onOpenDocument?: (documentId: number, page: number) => void
}

/**
 * Occurrences for the selected term. The old UI opened each one in a modal;
 * here every row shows the full sentence inline with the surface form
 * highlighted, so scanning usage does not cost a click each.
 */
export function MentionsList({
  mentions,
  isLoading,
  termValue,
  onOpenDocument,
}: MentionsListProps) {
  const { t } = useTranslation()
  const parentRef = useRef<HTMLDivElement>(null)
  const [filter, setFilter] = useState('')

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase()
    return mentions.filter((m) => {
      if (!f) return true
      return (
        m.sentence.toLowerCase().includes(f) || (m.document ?? '').toLowerCase().includes(f)
      )
    })
  }, [mentions, filter])

  const virtualizer = useVirtualizer({
    count: visible.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  })

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  if (!mentions.length) {
    return <EmptyState title={t('mentions.empty')} />
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mentions.filter')}
          className="h-7 flex-1"
        />
      </div>

      <div className="border-b border-border px-3 py-1 text-[11px] text-muted-foreground">
        {visible.length === mentions.length
          ? t('units.mentions', { count: visible.length })
          : t('mentions.countOfTotal', {
              shown: formatNumber(visible.length),
              total: formatNumber(mentions.length),
            })}
      </div>

      <div ref={parentRef} className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
          {virtualizer.getVirtualItems().map((row) => {
            const m = visible[row.index]
            const href = sourceLink(m.url, m.surface)
            return (
              <div
                key={row.index}
                ref={virtualizer.measureElement}
                data-index={row.index}
                className="absolute left-0 top-0 w-full border-b border-border/60 px-3 py-2"
                style={{ transform: `translateY(${row.start}px)` }}
              >
                <div className="mb-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  {/* The title opens the document itself — the PDF is not
                      kept, but its full text with this term lit up is. */}
                  {onOpenDocument && m.document_id ? (
                    <button
                      type="button"
                      onClick={() => onOpenDocument(m.document_id!, m.page)}
                      className="truncate font-medium text-left hover:text-primary hover:underline"
                      title={t('mentions.openDocument')}
                    >
                      {m.document}
                    </button>
                  ) : (
                    <span className="truncate font-medium">{m.document}</span>
                  )}
                  {m.page > 0 && (
                    <span className="shrink-0">{t('common.page', { page: m.page })}</span>
                  )}
                  {/* The stored PDF itself, opened at this page. */}
                  {m.has_file && m.document_id && (
                    <a
                      href={documentFileUrl(m.document_id, m.page, m.surface || termValue)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-primary hover:underline"
                      title={t('mentions.openPdf')}
                    >
                      <FileText className="size-3" />
                    </a>
                  )}
                  {href && (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-primary hover:underline"
                      title={t('mentions.openSource')}
                    >
                      <ExternalLink className="size-3" />
                    </a>
                  )}
                </div>
                <p className="text-[13px] leading-snug">
                  {highlightSegments(m.sentence, m.surface || termValue).map((seg, i) =>
                    seg.match ? (
                      <mark
                        key={i}
                        className={cn(
                          'rounded-sm bg-highlight px-0.5 font-medium text-highlight-foreground',
                        )}
                      >
                        {seg.text}
                      </mark>
                    ) : (
                      <span key={i}>{seg.text}</span>
                    ),
                  )}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
